import { useState } from "react";
import MapChart from "./MapChart";
import { hasValidCoordinates, SiteInfo } from "./types";
import { Typography } from "antd";

const { Text } = Typography;
const WorldMap = ({
  mapHeight,
  fullSitesData,
  subsetSitesData,
}: {
  mapHeight: number;
  fullSitesData: SiteInfo[];
  subsetSitesData: SiteInfo[];
}) => {
  const [selectedSite, setSelectedSite] = useState<SiteInfo | undefined>(
    undefined,
  );

  const hasSiteWithCoordinates = fullSitesData.some(hasValidCoordinates);
  return (
    <div>
      <div
        className="w-full relative grid grid-cols-7 gap-2"
        style={{ height: `${mapHeight}px` }}
      >
        <div className="col-span-5 w-full relative ">
          <MapChart
            mapHeight={mapHeight}
            selectedSite={selectedSite}
            fullSitesData={fullSitesData}
            subsetSitesData={subsetSitesData}
            setSelectedSite={setSelectedSite}
          />
        </div>
        <div className="col-span-2 ">
          <MapSidePane
            hasSiteWithCoordinates={hasSiteWithCoordinates}
            siteInfo={selectedSite}
          />
        </div>
      </div>
    </div>
  );
};

const MapSidePane = ({
  siteInfo,
  hasSiteWithCoordinates,
}: {
  hasSiteWithCoordinates: boolean;
  siteInfo: SiteInfo | undefined;
}) => {
  if (!siteInfo)
    return (
      <div
        data-testid="sites-overview-site-information-panel"
        className="flex flex-col h-full overflow-y-auto bg-white rounded-lg shadow-xs p-6"
      >
        <h2 className="text-[18px] font-bold mb-4">Site Information</h2>
        <div className="border-b border-gray-200 mb-6"></div>
        <p className="text-[16px] text-gray-500">
          {hasSiteWithCoordinates
            ? "Select a site on the map to view site information."
            : "No Site Coordinates Data to Display"}
        </p>
      </div>
    );

  return (
    <div
      data-testid="sites-overview-site-information-panel"
      className="flex flex-col h-full overflow-y-auto bg-white rounded-lg shadow-xs p-6"
    >
      <h2 className="text-[18px] font-bold mb-4">Site Information</h2>

      <div className="border-b border-gray-200 mb-6"></div>

      <div className="flex items-center mb-6 gap-2">
        <div className="w-[40px] h-[40px] rounded-full overflow-hidden border border-gray-200 flex items-center justify-center bg-gray-50">
          <img
            src={`https://hatscripts.github.io/circle-flags/flags/${siteInfo.country_code.toLowerCase()}.svg`}
            alt={`${siteInfo.country} flag`}
            crossOrigin="anonymous"
            className="w-full h-auto"
          />
        </div>
        <div className="flex-1">
          <h3 className="text-[16px]  text-[#2B4752] font-medium leading-4">
            {siteInfo.site_name}
          </h3>
        </div>
      </div>

      <div className="space-y-4">
        <PanelSection title="Site" info={siteInfo.site_name} />
        <PanelSection title="Region" info={siteInfo.region} />
        <PanelSection title="Country" info={siteInfo.country} />
        <PanelSection title="Date Activated" info={siteInfo.date_activated} />
        <PanelSection title="Address" info={siteInfo.address} />
        <PanelSection title="Contact" info={siteInfo.investigator_name} />
        <PanelSection title="Email" info={siteInfo.investigator_email} />
      </div>
    </div>
  );
};

const PanelSection = ({ title, info }: { title: string; info: string }) => {
  return (
    <div className="border rounded-md py-1 px-2 flex gap-2 w-fit items-center max-w-full ">
      <span className="text-gray-500 text-[14px]">{title}</span>

      <Text
        ellipsis={{
          tooltip: true,
        }}
        className="font-medium  text-[16px]"
      >
        {info}
      </Text>
    </div>
  );
};

export default WorldMap;
